import type { Attributes, Histogram, Meter } from "@opentelemetry/api";
import { context, Span, SpanStatusCode, trace } from "@opentelemetry/api";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import type { PushMetricExporter } from "@opentelemetry/sdk-metrics";
import { MeterProvider, PeriodicExportingMetricReader } from "@opentelemetry/sdk-metrics";
import type { SpanProcessor } from "@opentelemetry/sdk-trace-web";
import { BatchSpanProcessor, WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION, ATTR_USER_AGENT_ORIGINAL } from "@opentelemetry/semantic-conventions";
import { ATTR_DEPLOYMENT_ENVIRONMENT_NAME, ATTR_ENDUSER_ID } from "@opentelemetry/semantic-conventions/incubating";

import { ErrorKind } from "features/render/sceneError/types";
import { Platform } from "slices/explorer/types";
import { isAppBuild } from "utils/buildType";
import { log } from "utils/log";
import { type PerfResult, setMeasurementSink } from "utils/performanceMeasure";

import type { SpanNameMap } from "./config";
import { resolveTelemetryConfig, SPAN_NAMES } from "./config";

/** Structured failure details passed to {@link TelemetryManager.endViewerLoad} and {@link TelemetryManager.endAuth}. */
export interface TelemetryFailureDetails {
    /** One of the {@link ErrorKind} enum values; recorded as the "error.kind" span attribute. */
    errorKind: ErrorKind;
    /** HTTP status code when available; recorded as the "error.status_code" span attribute. */
    statusCode?: number;
    /**
     * Human-readable description used as the OpenTelemetry span status message.
     * Defaults to `errorKind` when omitted.
     */
    message?: string;
}

const l = log.telemetry;

/** The action the user took when leaving the error screen. */
export type ErrorRecoveryAction = "refresh" | "switch_user" | "close_tab";

/** Who triggered a viewpoint load: the SDK internals or an end-user action. */
export type ViewpointSource = "system" | "user";

/** High-level error category for {@link TelemetryManager.trackCaughtException}. */
export type ErrorCategory =
    | "init" // scene load, canvas creation, GPU detection, config loading
    | "auth" // core app authentication
    | "render" // engine interactions (canvas events, picks, measurements...)
    | "data" // domain data (search, properties, viewpoints... )
    | "integration"; // third-party services (Jira, BIMCollab...)

/** How often (ms) aggregated render-perf metrics are flushed to Mixpanel + OTEL */
export const RENDER_PERF_REPORT_INTERVAL_MS = 60_000;

/**
 * Bucket boundaries (ms) for the `viewer.ttfr_ms` histogram.
 *
 * Sized for whole-viewer-load latency, which runs from a few hundred ms on a warm
 * desktop cache to tens of seconds on a cold mobile connection. Percentiles are
 * interpolated within a bucket and cannot be resolved at all inside the unbounded
 * overflow bucket, so the top boundary has to sit above the slowest loads worth
 * measuring rather than at the SDK default of 10s.
 */
export const TTFR_BUCKET_BOUNDARIES_MS = [
    100, 250, 500, 1_000, 2_000, 3_000, 5_000, 7_500, 10_000, 15_000, 20_000, 30_000, 60_000,
];

/** Raw render-performance sample recorded by RenderPerfSampler (~5s) or tests */
export interface RenderPerfSample {
    fps: number;
    cpuTime: number;
    gpuTime: number;
    primitives: number;
}

/**
 * Test-only configuration for {@link TelemetryManager}.
 *
 * When provided, the constructor skips the `isAppBuild` / region guards and
 * wires the real OTel SDK to the supplied in-memory exporters.  This lets
 * tests exercise the full span/metric pipeline without HTTP calls or module
 * mocking.
 *
 * @example
 * ```ts
 * import { InMemorySpanExporter, SimpleSpanProcessor } from "@opentelemetry/sdk-trace-web";
 * import { AggregationTemporality, InMemoryMetricExporter } from "@opentelemetry/sdk-metrics";
 *
 * const spanExporter = new InMemorySpanExporter();
 * const tm = new TelemetryManager({
 *     spanProcessor: new SimpleSpanProcessor(spanExporter),
 *     metricExporter: new InMemoryMetricExporter(AggregationTemporality.CUMULATIVE),
 * });
 * ```
 */
export interface TelemetryTestConfig {
    spanProcessor: SpanProcessor;
    metricExporter: PushMetricExporter;
    metricExportIntervalMs?: number;
    /**
     * Endpoint for the sendBeacon crash path. The beacon bypasses the injected
     * span processor and posts OTLP JSON directly, so {@link beaconCrashSignal}
     * is inert unless a test supplies an endpoint here.
     */
    traceEndpoint?: string;
}

/**
 * Coerce arbitrary measurement metadata into OTel-safe span attributes.
 *
 * Primitive values (string/number/boolean) pass through unchanged; everything
 * else is JSON-serialized so an unsupported attribute type can never throw.
 * `null`/`undefined` entries are dropped.
 */
function toAttributes(meta: Record<string, unknown>): Attributes {
    const attrs: Attributes = {};

    for (const [key, value] of Object.entries(meta)) {
        if (value === undefined || value === null) {
            continue;
        }

        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
            attrs[key] = value;
        } else {
            try {
                const serialized = JSON.stringify(value);
                attrs[key] = serialized === undefined ? String(value) : serialized;
            } catch {
                attrs[key] = String(value);
            }
        }
    }

    return attrs;
}

/** Convert OTel {@link Attributes} into the OTLP/HTTP-JSON `KeyValue[]` representation. */
function attributesToOtlp(attributes: Attributes): Array<{ key: string; value: Record<string, unknown> }> {
    const result: Array<{ key: string; value: Record<string, unknown> }> = [];

    for (const [key, value] of Object.entries(attributes)) {
        if (value === undefined || value === null) {
            continue;
        }

        if (typeof value === "boolean") {
            result.push({ key, value: { boolValue: value } });
        } else if (typeof value === "number") {
            // int64 values must be encoded as strings per the OTLP/JSON spec.
            result.push({
                key,
                value: Number.isInteger(value) ? { intValue: String(value) } : { doubleValue: value },
            });
        } else {
            result.push({ key, value: { stringValue: String(value) } });
        }
    }

    return result;
}

/** Generate a random lowercase-hex string of `byteLength` bytes (e.g. 16 → 32 chars for a trace id). */
function randomHex(byteLength: number): string {
    const bytes = new Uint8Array(byteLength);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export class TelemetryManager {
    private tracer?: ReturnType<typeof trace.getTracer>;
    private meter?: Meter;
    private tracerProvider?: WebTracerProvider;
    private meterProvider?: MeterProvider;
    private isEnabled: boolean = false;
    private platform?: Platform;

    /** Attributes stamped on every span (mirrors Mixpanel `register` super-properties). */
    private globalAttributes: Record<string, string | number | boolean> = {};
    /** Identified user id (mirrors Mixpanel `identify`). */
    private userId?: string;

    /** Monotonically increasing counter stamped on widget interaction events for sequence reconstruction. */
    private widgetEventSequence: number = 0;
    /** Timestamp (ms) when the TelemetryManager was created; used to compute session-relative elapsed time. */
    private readonly sessionStartMs: number = Date.now();

    /** OTEL histograms for render-performance metrics */
    private fpsHistogram?: Histogram;
    private cpuTimeHistogram?: Histogram;
    private gpuTimeHistogram?: Histogram;
    private primitivesHistogram?: Histogram;
    /** OTEL histogram for time-to-first-render (viewer-load latency, not a render-perf sample) */
    private ttfrHistogram?: Histogram;
    /** Low-cardinality dimensions applied to every render-performance sample. */
    private renderPerfAttributes: Attributes = {};

    /**
     * Trace-collector endpoint and resource metadata captured at construction.
     * Retained so {@link beaconCrashSignal} can hand-roll an OTLP payload and send
     * it via `navigator.sendBeacon` — the only transport that survives the abrupt
     * WebView teardown that accompanies an Android OOM crash (the BatchSpanProcessor
     * cannot flush over HTTP once the page is being killed).
     */
    private traceEndpoint?: string;
    private resourceAttributes?: Record<string, string>;
    private scopeName?: string;

    /** Resolved span names — standalone vs SDK depending on build type. */
    private spanNames: SpanNameMap = SPAN_NAMES;

    private uninstallGlobalHandlers: () => void = () => {};
    private uninstallFlushHandlers: () => void = () => {};
    private inFlightFlush?: Promise<void>;

    constructor(testConfig?: TelemetryTestConfig) {
        try {
            if (testConfig) {
                // ── Test path: skip guards, use injected exporters ────────────
                const resourceAttributes: Record<string, string> = {
                    [ATTR_SERVICE_NAME]: "test-service",
                    [ATTR_SERVICE_VERSION]: "test",
                };
                const resource = resourceFromAttributes(resourceAttributes);

                if (testConfig.traceEndpoint) {
                    this.traceEndpoint = testConfig.traceEndpoint;
                    this.resourceAttributes = resourceAttributes;
                    this.scopeName = "test-lib";
                }

                const provider = new WebTracerProvider({
                    resource,
                    spanProcessors: [testConfig.spanProcessor],
                });

                this.tracerProvider = provider;
                this.tracer = provider.getTracer("test-lib");

                const meterProvider = new MeterProvider({
                    resource,
                    readers: [
                        new PeriodicExportingMetricReader({
                            exporter: testConfig.metricExporter,
                            exportIntervalMillis: testConfig.metricExportIntervalMs ?? RENDER_PERF_REPORT_INTERVAL_MS,
                        }),
                    ],
                });

                this.meterProvider = meterProvider;
                this.meter = meterProvider.getMeter("test-lib");
                this._registerHistograms();

                this.isEnabled = true;
                this._installFlushOnHide();
                return;
            }

            // ── Production path ───────────────────────────────────────────────
            const config = resolveTelemetryConfig();

            if (!config) {
                l.debug("Disabled on localhost");
                return;
            }

            const { endpoint, metricsEndpoint, serviceName, libraryName, region, version, spanNames } = config;
            this.spanNames = spanNames;

            const resourceAttributes: Record<string, string> = {
                [ATTR_SERVICE_NAME]: serviceName,
                [ATTR_SERVICE_VERSION]: version,
                [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: region,
                [ATTR_USER_AGENT_ORIGINAL]: navigator.userAgent,
            };
            const resource = resourceFromAttributes(resourceAttributes);

            // Retained for the sendBeacon-based crash reporter (see beaconCrashSignal).
            this.traceEndpoint = endpoint;
            this.resourceAttributes = resourceAttributes;
            this.scopeName = libraryName;

            const exporter = new OTLPTraceExporter({
                url: endpoint,
                headers: { "Content-Type": "application/json" },
            });

            const provider = new WebTracerProvider({
                resource,
                spanProcessors: [new BatchSpanProcessor(exporter)],
            });

            // IMPORTANT: We use a local provider (without global registration) to avoid conflicts
            // with host applications (e.g., ui-service-shell) that may already have OpenTelemetry
            // initialized. This ensures our resource attributes (service.name="bim-webviewer-sdk", version,
            // region) are preserved and our spans export correctly to our regional collectors.
            //
            // Note: We intentionally do not register a context manager, so OpenTelemetry will not
            // automatically propagate parent/child relationships across async boundaries. All spans
            // we create are either root spans or explicit children created by passing a parent
            // Context (for example, in startPhase()). If you need implicit parent-child span
            // relationships across async boundaries in the future, you would need to add:
            // provider.register({ contextManager: new StackContextManager() });
            this.tracerProvider = provider;
            this.tracer = provider.getTracer(libraryName);
            // Tracing initialized successfully; enable telemetry overall.
            this.isEnabled = true;

            // ── Metrics provider ─────────────────────────────────────────────
            try {
                const metricsExporter = new OTLPMetricExporter({
                    url: metricsEndpoint,
                    headers: { "Content-Type": "application/json" },
                });

                const meterProvider = new MeterProvider({
                    resource,
                    readers: [
                        new PeriodicExportingMetricReader({
                            exporter: metricsExporter,
                            exportIntervalMillis: RENDER_PERF_REPORT_INTERVAL_MS,
                        }),
                    ],
                });

                this.meterProvider = meterProvider;
                this.meter = meterProvider.getMeter(libraryName);
                this._registerHistograms();
            } catch (metricsError) {
                // Metrics failed to initialize; keep tracing enabled.
                l.warn("Failed to initialize metrics; tracing remains enabled:", metricsError);
            }

            this._installFlushOnHide();
            l.debug(`Initialized v${version} for region: ${region}`);
        } catch (error) {
            // Graceful degradation: telemetry fails silently, app continues
            l.warn("Failed to initialize, telemetry disabled:", error);
            this.isEnabled = false;
        }
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private _registerHistograms(): void {
        if (!this.meter) {
            return;
        }

        this.fpsHistogram = this.meter.createHistogram("render.fps", {
            description: "Frames per second",
            unit: "fps",
        });
        this.cpuTimeHistogram = this.meter.createHistogram("render.cpu_time_ms", {
            description: "CPU render draw time",
            unit: "ms",
        });
        this.gpuTimeHistogram = this.meter.createHistogram("render.gpu_time_ms", {
            description: "GPU render draw time",
            unit: "ms",
        });
        this.primitivesHistogram = this.meter.createHistogram("render.primitives", {
            description: "Primitive count",
            unit: "1",
        });
        this.ttfrHistogram = this.meter.createHistogram("viewer.ttfr_ms", {
            description: "Time from viewer-load start until the engine draws its first frame",
            unit: "ms",
            // The SDK default boundaries top out at 10s, which is the wrong scale for a
            // whole-load measurement: everything slower collapses into one unbounded
            // bucket, so P99 cannot be resolved past 10s. The render.* histograms above
            // keep the defaults because they measure per-frame values in the 0-100 range.
            advice: { explicitBucketBoundaries: TTFR_BUCKET_BOUNDARIES_MS },
        });
    }

    private _installFlushOnHide(): void {
        if (typeof document === "undefined" || typeof window === "undefined") {
            return;
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                void this.forceFlush();
            }
        };
        const onPageHide = () => void this.forceFlush();

        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("pagehide", onPageHide);
        this.uninstallFlushHandlers = () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("pagehide", onPageHide);
        };
    }

    /**
     * Install global error handlers for unhandled exceptions and promise rejections.
     * Only called for standalone (app) builds to avoid conflicting with host app handlers.
     */
    installStandaloneGlobalHandlers(): void {
        if (!this.isEnabled) {
            return;
        }

        const onError = (event: ErrorEvent) => {
            this.trackEvent(
                "unhandled_error",
                {
                    "error.source": "onerror",
                    "error.message": event.message || "Unknown error",
                    ...(event.filename ? { "error.filename": event.filename } : {}),
                    ...(event.lineno ? { "error.lineno": event.lineno } : {}),
                    ...(event.colno ? { "error.colno": event.colno } : {}),
                },
                event.error instanceof Error ? event.error : undefined,
            );
        };

        const onRejection = (event: PromiseRejectionEvent) => {
            const error = event.reason instanceof Error ? event.reason : undefined;
            const message = error?.message ?? (typeof event.reason === "string" ? event.reason : "Unknown rejection");

            this.trackEvent(
                "unhandled_error",
                { "error.source": "unhandledrejection", "error.message": message },
                error,
            );
        };

        window.addEventListener("error", onError);
        window.addEventListener("unhandledrejection", onRejection);

        this.uninstallGlobalHandlers = () => {
            window.removeEventListener("error", onError);
            window.removeEventListener("unhandledrejection", onRejection);
        };

        l.debug("Global error handlers installed");
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Record a single render-performance sample (called by always-on {@link RenderPerfSampler}).
     *
     * Each value is recorded on its corresponding OTEL histogram. The SDK's
     * {@link PeriodicExportingMetricReader} aggregates and exports the distribution
     * (min, max, sum, count, bucket counts) every {@link RENDER_PERF_REPORT_INTERVAL_MS}.
     *
     * The method is a no-op when telemetry is disabled (LOCAL region).
     *
     * @param sample - fps, cpuTime, gpuTime, primitives measured at this tick
     */
    recordRenderPerf(sample: RenderPerfSample): void {
        if (!this.isEnabled) {
            return;
        }

        if (!Object.values(sample).every(Number.isFinite)) {
            l.warn("Ignoring non-finite render performance sample");
            return;
        }

        const attributes = this._getRenderPerfAttributes();
        this.fpsHistogram?.record(sample.fps, attributes);
        this.cpuTimeHistogram?.record(sample.cpuTime, attributes);
        this.gpuTimeHistogram?.record(sample.gpuTime, attributes);
        this.primitivesHistogram?.record(sample.primitives, attributes);
    }

    /**
     * Record time to first render — when the engine draws its first frame.
     *
     * This is engine readiness, not perceived readiness: an opaque loading overlay
     * still covers the canvas at this point and stays up until the scene settles, so
     * the user cannot see the drawn frame yet. Treat it as the lower bound on the
     * wait, and do not read it as time-to-visible-pixels.
     *
     * Emitted as a metric rather than read off the viewer-load span because
     * successful loads are sampled 1:100 at the collectors while metrics are
     * not sampled at all, so only the metric gives trustworthy percentiles.
     * The span carries the same value for per-session drill-down.
     *
     * @param ms         - Milliseconds from viewer-load start to the first drawn frame.
     * @param attributes - Dimensions to slice by, e.g. device class.
     */
    recordTimeToFirstRender(ms: number, attributes?: Attributes): void {
        if (!this.isEnabled) {
            return;
        }

        // A non-finite value would corrupt the histogram sum for every later query;
        // the SDK only rejects non-numbers.
        if (!Number.isFinite(ms)) {
            l.warn(`Ignoring non-finite time to first render: ${ms}`);
            return;
        }

        this.ttfrHistogram?.record(ms, attributes);

        l.debug(`Time to first render: ${ms}ms`);
    }

    /**
     * Force an immediate export of pending metrics.
     *
     * Called on page hide so once-per-session metrics are not lost to the periodic
     * reader's export window. Safe to call when metrics failed to initialize.
     */
    async flushMetrics(): Promise<void> {
        if (!this.isEnabled || !this.meterProvider) {
            return;
        }

        try {
            await this.meterProvider.forceFlush();
        } catch (error) {
            l.warn("Failed to flush metrics:", error);
        }
    }

    /**
     * Begin a span for a `startMeasure()` measurement, named `perf.<name>`.
     *
     * When `parentSpan` is provided the new span is linked to it via explicit
     * context propagation (the same mechanism as {@link startPhase}), so nested
     * measurements form a parent/child trace. When it is null the span is a
     * root span. The returned span is stored as the measurement's opaque handle
     * and later passed to {@link endMeasurement}.
     *
     * Wired up via {@link setMeasurementSink}; not called directly by app code.
     *
     * @param name       - The measurement name (becomes `perf.<name>`).
     * @param parentSpan - Enclosing measurement's span, or null for a root span.
     * @returns The started span, or null when telemetry is disabled.
     */
    startMeasurement(name: string, parentSpan: Span | null): Span | null {
        if (!this.isEnabled || !this.tracer) {
            return null;
        }

        let span: Span;
        if (parentSpan) {
            const parentContext = trace.setSpan(context.active(), parentSpan);
            span = this.tracer.startSpan(`perf.${name}`, {}, parentContext);
        } else {
            span = this.tracer.startSpan(`perf.${name}`, { root: true });
        }

        this._stampSpan(span);
        span.setAttribute("perf.name", name);

        return span;
    }

    /**
     * Finalize a measurement span started by {@link startMeasurement}.
     *
     * Stamps the total duration and top-level `meta` as attributes, replays each
     * `mark` as a span event positioned at its relative offset (carrying
     * `perf.elapsed_ms` plus any mark metadata), then ends the span.
     *
     * Because the span was started eagerly in {@link startMeasurement}, its
     * start/end timestamps reflect the real elapsed time without reconstruction.
     *
     * @param span   - The span returned from startMeasurement() (no-op when null).
     * @param result - The measurement returned by `PerfMeasurement.end()`.
     */
    endMeasurement(span: Span | null, result: PerfResult): void {
        if (!span) {
            return;
        }

        span.setAttribute("perf.total_ms", result.totalMs);

        if (result.meta) {
            for (const [key, value] of Object.entries(toAttributes(result.meta))) {
                if (value !== undefined) {
                    span.setAttribute(key, value);
                }
            }
        }

        // The span started ~totalMs ago; reconstruct that base so mark events
        // land at the correct offset within the span's timeline.
        const markBase = Date.now() - result.totalMs;
        for (const mark of result.marks) {
            span.addEvent(
                mark.label,
                { "perf.elapsed_ms": mark.elapsedMs, ...(mark.meta ? toAttributes(mark.meta) : {}) },
                markBase + mark.elapsedMs,
            );
        }

        const isError = result.meta?.error === true;
        span.setStatus({ code: isError ? SpanStatusCode.ERROR : SpanStatusCode.OK });
        span.end();

        l.debug(`Measurement recorded: ${result.name} (${result.totalMs.toFixed(1)}ms)`);
    }

    /**
     * Set the platform that will be stamped on every subsequent span.
     * Must be called before any spans are started (typically during Webviewer init).
     *
     * @param platform - The platform value from InitOptions (e.g. Platform.Web, Platform.iOS, Platform.Android).
     */
    setPlatform(platform: Platform): void {
        this.platform = platform;
        l.debug(`Platform set to: ${platform}`);
    }

    /**
     * Set low-cardinality dimensions for render-performance metrics.
     * Do not add scene, organization, or user identifiers here.
     */
    setRenderPerfAttributes(attributes: Attributes): void {
        this.renderPerfAttributes = { ...attributes };
    }

    /**
     * Flush any pending telemetry and stop all background timers/exporters.
     * NOTE: shutdown() is a test-cleanup affordance, not a production API.
     *
     * Call this when the TelemetryManager is no longer needed (e.g. in test
     * teardown) to prevent leaked timers from the {@link PeriodicExportingMetricReader}
     * and the {@link BatchSpanProcessor}.
     *
     * Safe to call multiple times or when telemetry is disabled (no-op).
     */
    async shutdown(): Promise<void> {
        if (!this.isEnabled) {
            return;
        }

        this.isEnabled = false;
        this.uninstallGlobalHandlers();
        this.uninstallFlushHandlers();

        const results = await Promise.allSettled([this.tracerProvider?.shutdown(), this.meterProvider?.shutdown()]);

        for (const result of results) {
            if (result.status === "rejected") {
                l.warn("Provider shutdown error:", result.reason);
            }
        }

        l.debug("Shutdown complete");
    }

    /** Immediately export queued spans and metric observations without disabling telemetry. */
    forceFlush(): Promise<void> {
        if (!this.isEnabled) {
            return Promise.resolve();
        }

        if (this.inFlightFlush) {
            return this.inFlightFlush;
        }

        const flushPromise = this._flushProviders();
        this.inFlightFlush = flushPromise;
        void flushPromise.finally(() => {
            if (this.inFlightFlush === flushPromise) {
                this.inFlightFlush = undefined;
            }
        });
        return flushPromise;
    }

    private async _flushProviders(): Promise<void> {
        const results = await Promise.allSettled([this.tracerProvider?.forceFlush(), this.meterProvider?.forceFlush()]);
        for (const result of results) {
            if (result.status === "rejected") {
                l.warn("Provider flush error:", result.reason);
            }
        }
    }

    /**
     * Stamp common attributes (platform, user.id, globalAttributes) on a span.
     * Called by both {@link createSpan} and {@link startPhase} so every span
     * gets a consistent set of base attributes.
     */
    private _stampSpan(span: Span): void {
        if (this.platform) {
            span.setAttribute("platform", this.platform);
        }

        if (this.userId) {
            // custom attribute consumed by Honeycomb
            span.setAttribute("user.id", this.userId);
            // OTEL semantic convention mapped to top-level UserId in Azure Monitor (standalone)
            span.setAttribute(ATTR_ENDUSER_ID, this.userId);
        }

        for (const [key, value] of Object.entries(this.globalAttributes)) {
            span.setAttribute(key, value);
        }
    }

    private _getRenderPerfAttributes(): Attributes {
        return this.platform ? { ...this.renderPerfAttributes, platform: this.platform } : this.renderPerfAttributes;
    }

    /**
     * Create a span with common attributes (e.g. platform) stamped automatically.
     * All public start*() methods should use this instead of this.tracer.startSpan() directly
     * so that base attributes are applied consistently to every span.
     */
    private createSpan(name: string): Span | null {
        if (!this.isEnabled || !this.tracer) {
            return null;
        }

        const span = this.tracer.startSpan(name);
        this._stampSpan(span);

        return span;
    }

    /**
     * Start viewer load span - tracks initialization performance and success
     *
     * @param sceneId - Optional scene/model ID being loaded
     * @returns The span to be ended when initialization completes, or null if telemetry is disabled
     */
    startViewerLoad(sceneId?: string): Span | null {
        const span = this.createSpan(this.spanNames.VIEWER_LOAD);
        if (!span) {
            return null;
        }

        if (sceneId) {
            span.setAttribute("scene.id", sceneId);
        }

        l.debug("Viewer load started");
        return span;
    }

    /**
     * Start a child span representing a named initialization phase.
     *
     * The child span is linked to the parent via explicit context propagation
     * (no global context manager required). This gives native waterfall
     * visualisation, per-phase error attribution, and queryability in tracing
     * back-ends (Jaeger, Honeycomb, etc.).
     *
     * @param parentSpan - The parent span (e.g. from startViewerLoad). No-op when null.
     * @param name       - Phase name; the child span will be named `phase.<name>`.
     * @returns The child span, or null if telemetry is disabled / parentSpan is null.
     */
    startPhase(parentSpan: Span | null, name: string): Span | null {
        if (!parentSpan || !this.isEnabled || !this.tracer) {
            return null;
        }

        const parentContext = trace.setSpan(context.active(), parentSpan);
        const childSpan = this.tracer.startSpan(`phase.${name}`, {}, parentContext);
        this._stampSpan(childSpan);

        l.debug(`Phase "${name}" started`);
        return childSpan;
    }

    /**
     * End a child phase span previously started with {@link startPhase}.
     *
     * @param span    - The phase span to close (no-op when null).
     * @param success - Whether the phase completed successfully (default true).
     * @param error   - Optional Error to record when success is false.
     */
    endPhase(span: Span | null, success: boolean = true, error?: Error): void {
        if (!span) {
            return;
        }

        if (success) {
            span.setStatus({ code: SpanStatusCode.OK });
        } else {
            span.setStatus({
                code: SpanStatusCode.ERROR,
                message: error?.message,
            });
            if (error) {
                span.recordException(error);
            }
        }

        span.end();
        l.debug(`Phase ended: ${success ? "OK" : "ERROR"}`);
    }

    /**
     * Null-safe wrapper around {@link Span.setAttribute}.
     *
     * Use this when you hold a raw `Span | null` reference and want to stamp a
     * single attribute without an inline null-check at every call site.
     *
     * @param span  - The span to stamp (no-op when null).
     * @param key   - Attribute key.
     * @param value - Attribute value.
     */
    setSpanAttribute(span: Span | null, key: string, value: string | number | boolean): void {
        span?.setAttribute(key, value);
    }

    /**
     * End viewer load span with success/failure status.
     * This determines "good vs bad" for SLOs.
     *
     * @param span    - The span returned from startViewerLoad() (or null if telemetry disabled).
     * @param success - Whether initialization succeeded.
     * @param failure - Structured failure details (only meaningful when success is false).
     */
    endViewerLoad(span: Span | null, success: boolean, failure?: TelemetryFailureDetails): void {
        if (!span) {
            return; // Telemetry disabled, nothing to do
        }

        // CRITICAL: This attribute is used for Availability SLO
        // Query: WHERE name = "bim-webviewer-sdk.viewer.load" AND success = true
        span.setAttribute("success", success);

        if (failure?.errorKind !== undefined) {
            span.setAttribute("error.kind", failure.errorKind);
        }

        if (failure?.statusCode !== undefined) {
            span.setAttribute("error.status_code", failure.statusCode);
        }

        // Set OpenTelemetry status (used for error tracking)
        span.setStatus({
            code: success ? SpanStatusCode.OK : SpanStatusCode.ERROR,
            message: failure?.message ?? failure?.errorKind,
        });

        // span.end() automatically captures duration_ms for Performance SLO
        // Query: P95(duration_ms) WHERE name = "bim-webviewer-sdk.viewer.load" AND success = true
        span.end();

        l.debug(`Viewer load ended: ${success ? "SUCCESS" : "FAILED"}`);
    }

    /**
     * Record how the user responded to the error screen.
     *
     * Creates a single, immediately-ended span so that the action is visible
     * in the OTEL backend correlated with the scene load failure.
     *
     * @param errorKind - The ErrorKind that was displayed on the error screen.
     * @param action    - What the user did: clicked "Refresh"/"Switch User", or closed the tab.
     */
    recordErrorRecovery(errorKind: ErrorKind, action: ErrorRecoveryAction): void {
        const span = this.createSpan(this.spanNames.ERROR_RECOVERY);
        if (!span) {
            return;
        }

        span.setAttribute("error.kind", errorKind);
        span.setAttribute("recovery.action", action);
        span.setStatus({ code: SpanStatusCode.OK });
        span.end();

        l.debug(`Error recovery recorded: action=${action}, errorKind=${errorKind}`);
    }

    /**
     * Start an auth span – tracks the token-exchange / user-fetch step independently
     * from the viewer-load span that covers the full initialisation in useHandleInit.
     *
     * Keeping auth as its own span prevents double-counting sessions in the
     * Availability SLO (which only queries VIEWER_LOAD spans).
     *
     * @returns The span to be ended when auth completes, or null if telemetry is disabled.
     */
    startAuth(): Span | null {
        const span = this.createSpan(this.spanNames.AUTH);
        if (!span) {
            return null;
        }
        l.debug("Auth started");
        return span;
    }

    /**
     * End the auth span opened by startAuth().
     *
     * @param span    - The span returned from startAuth() (or null if telemetry disabled).
     * @param success - Whether authentication succeeded.
     * @param failure - Structured failure details (only meaningful when success is false).
     */
    endAuth(span: Span | null, success: boolean, failure?: TelemetryFailureDetails): void {
        if (!span) {
            return;
        }

        span.setAttribute("success", success);

        if (failure?.errorKind !== undefined) {
            span.setAttribute("error.kind", failure.errorKind);
        }

        if (failure?.statusCode !== undefined) {
            span.setAttribute("error.status_code", failure.statusCode);
        }

        span.setStatus({
            code: success ? SpanStatusCode.OK : SpanStatusCode.ERROR,
            message: failure?.message ?? failure?.errorKind,
        });

        span.end();

        l.debug(`Auth ended: ${success ? "SUCCESS" : "FAILED"}`);
    }

    // ── Product-analytics helpers (parallel Mixpanel) ─────────────────────

    /**
     * Identify the current user (mirrors Mixpanel `identify` + `people.set`).
     *
     * The userId is stamped as `user.id` and `enduser.id` on every span created
     * after this call. Additional properties (e.g. organization) are stored as
     * global attributes.
     *
     * @param userId     - Unique user identifier.
     * @param properties - Optional key/value pairs about the user (e.g. `{ "user.org": "Acme" }`).
     */
    identifyUser(userId: string, properties?: Record<string, string | number | boolean>): void {
        this.userId = userId;

        if (properties) {
            for (const [key, value] of Object.entries(properties)) {
                this.globalAttributes[key] = value;
            }
        }

        l.debug(`User identified: ${userId}`);
    }

    /**
     * Register global attributes stamped on every subsequent span
     * (mirrors Mixpanel `register` super-properties).
     *
     * @param properties - Key/value pairs to attach to all future spans.
     */
    setGlobalAttributes(properties: Record<string, string | number | boolean>): void {
        for (const [key, value] of Object.entries(properties)) {
            this.globalAttributes[key] = value;
        }

        l.debug("Global attributes registered", Object.keys(properties));
    }

    /**
     * Track a product-analytics event as a short-lived span
     * (mirrors Mixpanel `track`).
     *
     * A span is created, event-specific properties are set as attributes,
     * and the span is immediately ended so it is exported as a single
     * point-in-time event.
     *
     * @param eventName  - Human-readable event name (e.g. "Opened Widget").
     * @param properties - Optional attributes specific to this event.
     * @param error      - Optional Error to record. When provided the span status
     *                     is set to {@link SpanStatusCode.ERROR} and the exception
     *                     is attached; otherwise the span is marked OK.
     */
    trackEvent(eventName: string, properties?: Attributes, error?: Error): void {
        const span = this.createSpan(eventName);
        if (!span) {
            return;
        }

        if (properties) {
            for (const [key, value] of Object.entries(properties)) {
                if (value !== undefined) {
                    span.setAttribute(key, value);
                }
            }
        }

        if (error) {
            span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
            span.recordException(error);
        } else {
            span.setStatus({ code: SpanStatusCode.OK });
        }
        span.end();

        l.debug(`Event tracked: ${eventName}`);
    }

    /**
     * Track a caught exception as a short-lived error span.
     *
     * Creates a span named `"caught_exception"` with standardized attributes
     * for consistent querying across all catch blocks. Internally delegates to
     * {@link trackEvent} for span lifecycle management.
     *
     * @param options.source          - Call site identifier (e.g. `"useHandleInit.gpuDetection"`)
     * @param options.category        - High-level error category for dashboard filtering
     * @param options.error           - The caught value
     * @param options.severity        - `"warning"` (default) or `"error"`
     * @param options.extraAttributes - Additional span attributes beyond the standard set
     */
    trackCaughtException(options: {
        source: string;
        category: ErrorCategory;
        error: unknown;
        severity?: "warning" | "error";
        extraAttributes?: Attributes;
    }): void {
        const { source, category, error, severity = "warning", extraAttributes } = options;
        let err: Error;

        if (error instanceof Error) {
            err = error;
        } else {
            let msg: string;

            if (typeof error === "string") {
                msg = error;
            } else {
                try {
                    const json = JSON.stringify(error);
                    msg = json === undefined ? String(error) : json;
                } catch {
                    msg = String(error);
                }
            }

            err = new Error(msg);
        }

        const properties: Attributes = {
            "error.source": source,
            "error.category": category,
            "error.severity": severity,
        };

        if (extraAttributes) {
            for (const [key, value] of Object.entries(extraAttributes)) {
                if (value !== undefined) {
                    properties[key] = value;
                }
            }
        }

        this.trackEvent("caught_exception", properties, err);
    }

    /**
     * Report a crash signal via `navigator.sendBeacon` as a hand-rolled OTLP/HTTP
     * trace payload — the "last gasp" path.
     *
     * The regular {@link trackEvent} pipeline enqueues spans in a
     * {@link BatchSpanProcessor} that flushes asynchronously over HTTP. During an
     * Android OOM crash (or any abrupt WebView teardown) the page is killed before
     * that flush completes, so those spans never leave the device. `sendBeacon`
     * is the only transport browsers guarantee to deliver during page unload, so
     * this method mirrors the exact span shape ({@link createSpan} + platform /
     * user / global attributes) but serialises it directly to OTLP JSON.
     *
     * No-op when telemetry is disabled, when the endpoint is unknown, or when no
     * beacon/fetch transport is available.
     *
     * @param name       - Event/span name (e.g. `"webgl_context_lost"`).
     * @param attributes - Additional span attributes to merge in.
     */
    beaconCrashSignal(name: string, attributes?: Attributes): void {
        if (!this.isEnabled || !this.traceEndpoint || !this.resourceAttributes) {
            return;
        }

        // Mirror the base attributes _stampSpan() puts on every span, but as a
        // plain object since we serialise to OTLP JSON rather than an SDK span.
        const spanAttributes: Attributes = { ...this.globalAttributes };
        if (this.platform) {
            spanAttributes["platform"] = this.platform;
        }
        if (this.userId) {
            spanAttributes["user.id"] = this.userId;
            spanAttributes[ATTR_ENDUSER_ID] = this.userId;
        }
        Object.assign(spanAttributes, attributes, {
            // Marks the delivery path so beacon-delivered events can be told apart
            // from (and de-duplicated against) their batched-span counterparts.
            "telemetry.transport": "beacon",
        });

        try {
            const payload = this.buildOtlpTracePayload(name, spanAttributes);
            const body = JSON.stringify(payload);

            const hasNavigator =
                typeof globalThis.navigator === "object" && typeof globalThis.navigator?.sendBeacon === "function";
            const hasBlob = typeof globalThis.Blob === "function";

            if (hasNavigator && hasBlob) {
                const blob = new globalThis.Blob([body], { type: "application/json" });
                if (globalThis.navigator.sendBeacon(this.traceEndpoint, blob)) {
                    l.debug(`Crash signal beaconed: ${name}`);
                    return;
                }
            }

            // Fallback: keepalive fetch also survives page unload in most browsers.
            if (typeof globalThis.fetch === "function") {
                void globalThis
                    .fetch(this.traceEndpoint, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body,
                        keepalive: true,
                    })
                    .catch(() => {
                        // Best-effort; nothing more we can do while the page is dying.
                    });
            }
        } catch (error) {
            l.warn("Failed to beacon crash signal:", error);
        }
    }

    /** Build a minimal OTLP/HTTP-JSON trace payload carrying a single, already-ended span. */
    private buildOtlpTracePayload(name: string, attributes: Attributes): Record<string, unknown> {
        const nowNanos = `${Date.now()}000000`;

        return {
            resourceSpans: [
                {
                    resource: { attributes: attributesToOtlp(this.resourceAttributes ?? {}) },
                    scopeSpans: [
                        {
                            scope: { name: this.scopeName ?? "novoweb" },
                            spans: [
                                {
                                    traceId: randomHex(16),
                                    spanId: randomHex(8),
                                    name,
                                    // SPAN_KIND_INTERNAL
                                    kind: 1,
                                    startTimeUnixNano: nowNanos,
                                    endTimeUnixNano: nowNanos,
                                    attributes: attributesToOtlp(attributes),
                                    // STATUS_CODE_ERROR — crash signals are always errors. This is
                                    // also what keeps them: Refinery's "Keep All non-http errors"
                                    // rule retains error spans 1:1, so no keep flag is needed.
                                    status: { code: 2 },
                                },
                            ],
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Track a pageview event as a short-lived span
     * (mirrors Mixpanel `track_pageview`).
     *
     * @param properties - Optional attributes for the pageview (e.g. scene ID, title).
     */
    trackPageview(properties?: Record<string, string | number | boolean>): void {
        this.trackEvent("pageview", properties);
    }

    /**
     * Track a widget open or close interaction with sequencing metadata.
     *
     * Each call auto-stamps:
     * - `widget.sequence`           – monotonically increasing counter (1, 2, 3 …)
     *                                 enabling reconstruction of the exact open/close order.
     * - `widget.session_elapsed_ms` – milliseconds since session start for time-based analysis.
     * - `widget.action`             – `"open"` or `"close"`.
     * - `widget.open_widgets`       – comma-separated snapshot of widget keys that are open
     *                                 at the time of the event (after the action has been applied).
     * - `widget.open_count`         – number of widgets open at the time of the event.
     *
     * Additional event-specific properties (e.g. `close.reason`, `Duration`)
     * are merged in as extra span attributes.
     *
     * @param action          - `"open"` or `"close"`.
     * @param widgetKey       - The key of the widget being opened/closed.
     * @param openWidgets     - Snapshot of widget keys currently open (before dispatch).
     * @param extraProperties - Optional additional attributes for the span.
     */
    trackWidgetInteraction(
        action: "open" | "close",
        widgetKey: string,
        openWidgets: readonly string[],
        extraProperties?: Record<string, string | number | boolean>,
    ): void {
        this.widgetEventSequence++;

        const widgetsAfter =
            action === "open" ? [...openWidgets, widgetKey] : openWidgets.filter((w) => w !== widgetKey);

        this.trackEvent("Widget Interaction", {
            "widget.key": widgetKey,
            "widget.action": action,
            "widget.sequence": this.widgetEventSequence,
            "widget.session_elapsed_ms": Date.now() - this.sessionStartMs,
            "widget.open_widgets": widgetsAfter.join(","),
            "widget.open_count": widgetsAfter.length,
            ...extraProperties,
        });
    }

    /**
     * Start a viewpoint.loaded span – tracks the duration and outcome of applying
     * a viewpoint, whether triggered by the system (initial load) or by the user
     * (e.g. via the public `setViewpoint` API or bookmark selection).
     *
     * @param source - `"system"` for SDK-internal calls; `"user"` for end-user actions.
     * @returns The span to be ended when the viewpoint has been applied, or null if telemetry is disabled.
     */
    startViewpointLoad(source: ViewpointSource): Span | null {
        const span = this.createSpan(this.spanNames.VIEWPOINT_LOADED);
        if (!span) {
            return null;
        }
        span.setAttribute("viewpoint.source", source);
        l.debug(`Viewpoint load started (source=${source})`);
        return span;
    }

    /**
     * End a viewpoint.loaded span opened by {@link startViewpointLoad}.
     *
     * @param span    - The span returned from startViewpointLoad() (or null if telemetry disabled).
     * @param success - Whether the viewpoint was applied successfully.
     * @param errorMessage - Optional human-readable error message when success is false.
     */
    endViewpointLoad(span: Span | null, success: boolean, errorMessage?: string): void {
        if (!span) {
            return;
        }

        // CRITICAL: This attribute is used for viewpoint load success tracking.
        // Query: WHERE name = "bim-webviewer-sdk.viewpoint.loaded" AND success = true
        span.setAttribute("success", success);

        span.setStatus({
            code: success ? SpanStatusCode.OK : SpanStatusCode.ERROR,
            message: errorMessage,
        });

        // span.end() automatically captures duration_ms for Performance SLO
        span.end();

        l.debug(`Viewpoint load ended: ${success ? "SUCCESS" : "FAILED"}`);
    }
}

// Singleton instance - initialized once per page load
export const telemetry = new TelemetryManager();

// Map `startMeasure()` lifecycle onto OpenTelemetry spans, preserving the
// parent/child relationship between nested measurements. The opaque handle
// threaded through the sink is the measurement's span.
setMeasurementSink({
    onStart: (name, parent) => telemetry.startMeasurement(name, (parent as Span | null | undefined) ?? null),
    onEnd: (handle, result) => telemetry.endMeasurement((handle as Span | null | undefined) ?? null, result),
});

if (isAppBuild()) {
    telemetry.installStandaloneGlobalHandlers();
}
